import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import mockupData from '../mockup.json';

import Captcha from './Captcha';
import CustomFileInput from './CustomFileInput';
import FaqDropdown from './FaqDropdown';

import { Modal, Button, Divider, Form, InputField } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { getProfileData } from '@/services/editProfile';
import { submitFaqForm } from '@/services/faq';
import { SelectValue } from '@/types';
import { FaqBody } from '@/types/faq';
import { FAQ_SUBCAT, validateFieldError } from '@/utils';
import { faqFormSchema } from '@/utils/validators';

type FormValues = {
  nameAndSurName: string;
  email: string;
  category: {
    label: string;
    value: number;
  } | null;
  subcategory: {
    label: string;
    value: number;
    accion: string;
  } | null;
  comentario: string;
  blob: File | null;
};

const FaqForm = ({ isPublic }: { isPublic: boolean }) => {
  const { isMobile } = useIsDesktop();
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Obtener datos del perfil usando useQuery solo si no es público
  const { data: profileData, isLoading: isLoadingProfile } = useQuery(
    'profileData',
    async () => getProfileData(),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !isPublic, // No ejecutar la consulta si isPublic es true
      onError: (error) => {
        console.error('Error al obtener datos del perfil:', error);
      },
    }
  );

  const handleCaptchaVerify = (token: string | null) => {
    setCaptchaToken(token);
  };

  // Preparar las categorías para el dropdown
  const categoryOptions: SelectValue[] = mockupData.map((category) => ({
    label: category.titulo,
    value: category.id,
  }));

  // Estado para almacenar las subcategorías disponibles según la categoría seleccionada
  const [subcategoryOptions, setSubcategoryOptions] = useState<
    Array<{
      label: string;
      value: number;
      accion: string;
    }>
  >([]);

  // Función para obtener los valores iniciales del formulario
  const getInitialValues = () => ({
    nameAndSurName:
      !isPublic && profileData?.usuarioDT
        ? `${profileData.usuarioDT.nombres || ''} ${
            profileData.usuarioDT.apellido || ''
          }`.trim()
        : '',
    email:
      !isPublic && profileData?.usuarioDT?.email
        ? profileData.usuarioDT.email
        : '',
    category: null,
    subcategory: null,
    comentario: '',
    blob: null,
  });

  // Usar los datos del perfil para inicializar el formulario
  const formik = useFormik<FormValues>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: faqFormSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const idSubCatValue = FAQ_SUBCAT.find(
        (subcat) => subcat.value === values.subcategory?.value
      );
      const formData: FaqBody = {
        nombreApellido: values.nameAndSurName,
        email: values.email,
        idCat: values.category?.value,
        idSubCat: idSubCatValue?.label,
        accion: values.subcategory?.accion,
      };

      if (values.subcategory?.accion === 'MENSAJE') {
        formData.comentario = values.comentario;
        formData.blob = values.blob;
      }
      try {
        const response = await submitFaqForm(
          formData,
          values.subcategory?.accion ?? '',
          captchaToken ?? ''
        );
        if (response) {
          if (response.estado) {
            setIsModalOpen(true);
            // Reiniciar el formulario con los valores iniciales apropiados
            const initialValues = getInitialValues();
            formik.resetForm({ values: initialValues });
            setCaptchaToken(null);
            // Reiniciar el captcha
            if (window && (window as any).resetCaptcha) {
              (window as any).resetCaptcha();
            }
          }
          toast.show(response.mensaje);
        }
      } catch (error) {
        console.error(error);
      }
      setSubmitting(false);
    },
  });

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleBlur,
    handleChange,
    errors,
    touched,
  } = formik;

  // Actualizar las subcategorías cuando cambia la categoría seleccionada
  useEffect(() => {
    if (values.category) {
      const selectedCategory = mockupData.find(
        (cat) => cat.id === values.category?.value
      );
      if (selectedCategory) {
        const subOptions = selectedCategory.subcategorias.map((subcat) => ({
          label: subcat.texto,
          value: subcat.id,
          accion: subcat.accion,
        }));
        setSubcategoryOptions(subOptions);
        // Reset subcategory when category changes
        if (values.subcategory) {
          formik.setFieldValue('subcategory', null);
        }
        // Reset comentario when subcategory changes or category changes
        formik.setFieldValue('comentario', '');
      }
    } else {
      setSubcategoryOptions([]);
      // Reset comentario when category is cleared
      formik.setFieldValue('comentario', '');
    }
    // Importante: no incluir formik en las dependencias
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.category]);

  useEffect(() => {
    // Reset comentario when subcategory changes
    if (values.subcategory) {
      formik.setFieldValue('comentario', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.subcategory]);

  // Función personalizada para manejar el submit
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Invocar manualmente la función handleSubmit de formik
    handleSubmit(e);
  };

  return (
    <>
      {!isMobile && (
        <Divider
          className="my-5 bg-gdt-gray-5 lg:mr-[410px] lg:ml-[110px] w-auto space-y-4"
          type={'horizontal'}
        />
      )}

      <div
        className={
          isMobile
            ? 'm-4'
            : 'mt-4 lg:mr-[410px] lg:ml-[110px] bg-white rounded-lg shadow-sm mx-2 p-8 mb-8'
        }
      >
        <h1 className="font-medium text-[14px] mb-6">
          Si no encontraste la respuesta a tu consulta, podés enviarnos un
          mensaje a través de este formulario.
        </h1>

        <Form.Form
          className="space-y-[1.75rem] overflow-visible"
          loading={isSubmitting || (!isPublic && isLoadingProfile)}
          onSubmit={handleFormSubmit}
        >
          <div className="flex flex-col gap-y-4">
            <Form.Field>
              <InputField
                onlyLetters
                behavior="default"
                error={validateFieldError(
                  touched.nameAndSurName,
                  errors.nameAndSurName
                )}
                label="Nombre y Apellido*"
                maxLength={30}
                name="nameAndSurName"
                placeholder="Ingresar"
                value={values.nameAndSurName}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>

            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(touched.email, errors.email)}
                label="Correo electrónico*"
                name="email"
                placeholder="Ingresar"
                type="email"
                value={values.email}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>

            <Form.Field>
              <FaqDropdown
                error={validateFieldError(
                  !!touched.category,
                  errors.category?.toString()
                )}
                label="Categoría*"
                options={categoryOptions}
                placeholder="Ingresar"
                selected={values.category}
                onBlur={() => formik.setFieldTouched('category', true)}
                onChange={(option) => formik.setFieldValue('category', option)}
              />
            </Form.Field>

            {values.category && (
              <Form.Field>
                <FaqDropdown
                  error={validateFieldError(
                    !!touched.subcategory,
                    errors.subcategory?.toString() // value
                  )}
                  label="Seleccioná tu consulta*"
                  options={subcategoryOptions}
                  placeholder="Ingresar"
                  selected={values.subcategory}
                  onBlur={() => formik.setFieldTouched('subcategory', true)}
                  onChange={(option) =>
                    formik.setFieldValue('subcategory', option)
                  }
                />
              </Form.Field>
            )}
            {values.subcategory?.accion === 'MENSAJE' && (
              <>
                <Form.Field>
                  <InputField
                    behavior="textArea"
                    error={validateFieldError(
                      touched.comentario,
                      errors.comentario
                    )}
                    label="Dejanos tu mensaje*"
                    maxLength={500} // Optional: define a max length for the message
                    name="comentario"
                    placeholder="Mensaje"
                    rows={4}
                    value={values.comentario}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Field>
                <Form.Field>
                  <CustomFileInput
                    name="blob"
                    onChange={(event) => {
                      formik.setFieldValue(
                        'blob',
                        event.currentTarget.files &&
                          event.currentTarget.files[0]
                      );
                    }}
                  />
                  {/* You can add error display for blob if needed */}
                </Form.Field>
              </>
            )}
          </div>
          <div className="flex flex-col gap-y-4">
            <Button
              className="mb-4 max-w-[200px]"
              type="submit"
              variant="primary"
            >
              Enviar
            </Button>
            <Captcha
              sitekey="6Lcni6gUAAAAAJ-C3euJmgi6lOaOHhFJL7A1s5wN" // Reemplaza con tu clave de sitio de Google reCAPTCHA
              onVerify={handleCaptchaVerify}
            />
          </div>
        </Form.Form>
      </div>
      <Modal
        controlledOnClose={() => setIsModalOpen(false)}
        isControlledOpen={isModalOpen}
        render={() => (
          <div className="text-center">
            <p className="text-lg mb-4 mt-4">
              Tu consulta a Gran DT ya fue enviada.
            </p>
          </div>
        )}
        title="Muchas gracias"
        variant="controlled"
      />
    </>
  );
};

export default FaqForm;
